import { z } from 'zod';

const optionalNumber = z.preprocess((val) => {
  if (val === null || val === '' || val === undefined || val === 'null') {
    return undefined;
  }
  if (typeof val === 'number' && !isNaN(val)) {
    return val;
  }
  if (typeof val === 'string') {
    const num = Number(val.trim());
    return isNaN(num) ? undefined : num;
  }
  return undefined;
}, z.number().optional());

const optionalString = z.preprocess((val) => {
  if (val === null || val === undefined || val === 'null') {
    return undefined;
  }
  return String(val || '');
}, z.string().optional());

const optionalStringOrNumber = z.preprocess((val) => {
  if (val === null || val === undefined || val === 'null') {
    return undefined;
  }
  if (typeof val === 'string' && val.trim() === '') {
    return undefined;
  }
  return val;
}, z.union([z.string(), z.number()]).optional());

const optionalBoolean = z.preprocess((val) => {
  if (val === null || val === undefined || val === 'null') {
    return undefined;
  }
  return Boolean(val);
}, z.boolean().optional());

const itemSchema = z.object({
  id: z.union([z.string(), z.number()]),
  type: z.string(),
  order: z.number(),
  rawMaterial: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
  service: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
  resource: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
  productFinal: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
  company: z.object({ id: z.number(), name: z.string() }).nullable().optional(),
  unitOfMeasurementId: optionalNumber,
  weight: optionalNumber,
  idealMeasurements: optionalString,
  productScript: z.object({
    id: z.number().min(1, 'ID do script é obrigatório'),
    name: z.string().min(1, 'Nome do script é obrigatório')
  }).nullable().optional(),
  readyToExecute: z.preprocess(val => {
    if (val === 'true') return true;
    if (val === 'false') return false;
    return val;
  }, z.boolean().optional()),
  executionTime: optionalString,
  requiredQualification: optionalString,
  deliveryTime: optionalStringOrNumber,
  step: z.number().optional(),
}).refine(data => {
  if (data.type === 'rawMaterial') {
    return data.rawMaterial !== null && data.rawMaterial !== undefined;
  }
  return true;
}, {
  message: "Matéria-prima é obrigatória para este tipo de item",
  path: ['rawMaterial'],
}).refine(data => {
  if (data.type === 'service') {
    return data.service !== null && data.service !== undefined;
  }
  return true;
}, {
  message: "Serviço é obrigatório para este tipo de item",
  path: ['service'],
}).refine(data => {
  if (data.type === 'resource') {
    return data.resource !== null && data.resource !== undefined;
  }
  return true;
}, {
  message: "Insumo é obrigatório para este tipo de item",
  path: ['resource'],
}).refine(data => {
  if (data.type === 'productFinal') {
    return data.productFinal !== null && data.productFinal !== undefined;
  }
  return true;
}, {
  message: "Produto Manufaturado é obrigatório para este tipo de item",
  path: ['productFinal'],
}).refine(data => {
  if (data.type === 'productFinal' && data.productFinal) {
    return data.productScript !== null && data.productScript !== undefined && data.productScript.id && data.productScript.name;
  }
  return true;
}, {
  message: "Script do produto é obrigatório quando um produto manufaturado é selecionado",
  path: ['productScript'],
}).refine(data => {
  if (data.type === 'service') {
    return true;
  }
  return true;
}, {
  message: "",
  path: ['company'],
});

const scriptSchema = z.object({
  id: z.number(),
  name: z.string().min(1, 'Nome do script é obrigatório'),
  additionalDeadline: optionalNumber,
  cost: optionalNumber,
  items: z.array(itemSchema).min(1, 'O script deve ter pelo menos um item.'),
  productId: z.number().optional(),
  resources: z.array(z.any()).optional(),
  stages: z.array(z.any()).optional(),
  materials: z.array(z.any()).optional(),
  products: z.array(z.any()).optional(),
});

export const productFinalSchema = z.object({
  name: z.string().min(1, 'Nome do produto é obrigatório'),
  norm: optionalString,
  quantity: optionalNumber,
  productScript: optionalString,
  scripts: z.array(scriptSchema),
}).refine((data) => {
  return true;
}, {
  message: "Um produto não pode ser referenciado como item do seu próprio script",
});

export type ProductFinalSchemaType = z.infer<typeof productFinalSchema>;
export type ScriptSchemaType = z.infer<typeof scriptSchema>; 